import React from 'react';
import {Badge} from '@mantine/core';
import {Box} from 'src/design/atoms/Box';
import {Flex} from 'src/design/atoms/Flex';
import {SegmentedControl} from 'src/design/atoms/SegmentedControl';
import type {CourseAssignmentSimpleResponse} from 'src/api/ProactApiClient';
import {CourseAssignmentStateEnum} from 'src/api/ProactApiClient';
import {
  ASSIGNABLE_USER_STATES,
  OPEN_COURSE_ASSIGNMENT_STATES,
} from 'src/app/training/courseAssignmentFilters';
import {TrainingIcon} from 'src/app/training/coursePills';
import {SectionCard} from 'src/design/atoms/SectionCard';
import {AllClearState} from 'src/design/molecules/AllClearState';
import {DataList} from 'src/design/organisms/DataList';
import {useListState} from 'src/design/organisms/useListState';
import {PageHeaderSearch} from 'src/design/organisms/PageHeader';
import {useGetCourseAssignmentListQuery} from 'src/redux/queries/courseAssignment';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import useDebounce from 'src/shared/hooks/useDebounce';
import {CourseAssignmentRowPills} from 'src/app/training/courseAssignmentPillConfig';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

const COMPLETED_STATUSES = [
  CourseAssignmentStateEnum.Locked,
  CourseAssignmentStateEnum.Complete,
];

const MAX_ITEMS_PER_PAGE = 5;

export const MyCoursesCard: React.FC = () => {
  const navigate = useAppNavigate();
  const {
    searchTerm,
    setSearchTerm,
    filters,
    setFilters,
    currentPage,
    handlePageChange,
    start,
    length,
  } = useListState({
    identifier: 'my-courses',
    pageSize: MAX_ITEMS_PER_PAGE,
    defaultFilters: {showOpen: true},
  });
  const {showOpen} = filters;
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const stateFilters = showOpen ? OPEN_COURSE_ASSIGNMENT_STATES : COMPLETED_STATUSES;

  const {data, isLoading} = useGetCourseAssignmentListQuery({
    start,
    length,
    sortColumn: 'dueAt',
    sortDirection: 'desc',
    search: debouncedSearchTerm,
    states: stateFilters,
    activeStates: [true],
    viewCurrentUserOnly: true,
    userActiveStates: ASSIGNABLE_USER_STATES,
  });

  const courses = data?.data ?? [];
  const totalRecords = data?.totalRecords ?? 0;

  const handleToggle = (value: string) => {
    setFilters({showOpen: value === 'open'});
  };

  const handleNavigateTo = (id: string | number) =>
    navigate(
      PathUtility.TrainingCourseOverview(id as string)
    );

  const rightSection = (
    <Flex align="center" gap="sm" wrap="wrap">
      {totalRecords > 0 && (
        <Badge size="lg" variant="light" color="gray" circle>
          {totalRecords}
        </Badge>
      )}
      <SegmentedControl
        value={showOpen ? 'open' : 'completed'}
        onChange={handleToggle}
        data={[
          {label: 'Open', value: 'open'},
          {label: 'Completed', value: 'completed'},
        ]}
      />
      <PageHeaderSearch
        value={searchTerm}
        onChange={setSearchTerm}
        placeholder="Search courses..."
        width={240}
      />
    </Flex>
  );

  return (
    <SectionCard title="My Courses" rightSection={rightSection}>
      <Box px="sm" py="xs">
        {totalRecords === 0 && !isLoading && !searchTerm ? (
          <AllClearState />
        ) : (
          <DataList
            rowIcon={TrainingIcon}
            isLoading={isLoading}
            data={courses}
            currentPage={currentPage}
            totalRecords={totalRecords}
            recordsPerPage={MAX_ITEMS_PER_PAGE}
            onRowClick={handleNavigateTo}
            onPageChange={handlePageChange}
            onGetPillMapping={CourseAssignmentRowPills}
            onRowTextRender={(item: CourseAssignmentSimpleResponse) =>
              item.courseDefinition?.name ?? 'Unknown'
            }
            hasSearchTerm={searchTerm !== ''}
            hasCreationLogic={false}
            meta={undefined}
          />
        )}
      </Box>
    </SectionCard>
  );
};
